import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";
import styled from "styled-components";

import * as Fonts from "../../components/Fonts";
import { KeepoLoader } from "../../components/KeepoLoader";
import { useToast } from "../../components/Toast";
import PlusIcon from "../../icons/Plus";
import { isApiError } from "../../services/api/errors";
import { getPayments, PaymentRecipient, PaymentsState, reuseRecipient } from "../../services/payments";
import { SmallButton } from "../Domains/styles";
import { Card, Section } from "../Settings/styles";
import { NewSiteCard } from "../Sites/styles";
import { BankAccountModal } from "./BankAccountModal";
import { KycModal } from "./KycModal";
import { RecipientForm } from "./RecipientForm";
import { isKycStep, isProviderCode, isStopped, recipientStatusKey } from "./recipientStatus";
import { chargesPrefix, keepoNames, PaymentsLoad, prefix } from "./state";
import { Center, ErrorMessage, Message, Row, Status } from "./styles";

type Props = {
  siteId: string;
  payments: PaymentsLoad;
  setPayments: (state: PaymentsState) => void;
  isCurrent: (siteId: string) => boolean;
  /** On /cobrancas/dados-bancarios/novo: the registration form in place of the options. */
  isRegistering?: boolean;
  /** The form's header (back link and title), under its steps. */
  header?: ReactNode;
};

/**
 * Dados bancários: the registration (Pagar.me recipient) that receives this Keepo's online payments, its bank account
 * and the provider's review. Only for accounts in reais; the owner can reuse an approved registration from another of
 * their Keepos.
 */
export const Registration = ({ siteId, payments, setPayments, isCurrent, isRegistering = false, header }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  // The form has its own address (/novo): opening it and leaving it go through the browser's history
  const openForm = () => navigate("novo");
  // Cancelar takes the form's place in the history: the browser's back doesn't reopen it
  const closeForm = () => navigate("..", { relative: "path", replace: true });
  // The registration being reused (its button waits), and whether that failed
  const [reusingId, setReusingId] = useState<string>();
  const [reuseFailed, setReuseFailed] = useState(false);
  // The identity check opens from showKyc
  const [showKyc, setShowKyc] = useState(false);
  // A reload after the identity check failed: the card stays, with the load error under it
  const [reloadFailed, setReloadFailed] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);

  // Reads the Keepo's payments again (and shows them, unless the person switched Keepo meanwhile)
  const reload = (site: string) =>
    getPayments(site)
      .then(value => isCurrent(site) && setPayments(value))
      .catch(() => undefined);

  // The Keepo got its registration meanwhile (another tab or device): what is there is shown, not an error to retry
  const alreadyLinked = (site: string) => {
    toast.error(t(`${prefix}.errors.alreadyLinked`));
    reload(site);
  };

  const reuse = async (recipientId: string) => {
    // Only for the Keepo it was asked in: an answer after switching Keepo must not touch the new one's screen
    const site = siteId;
    setReuseFailed(false);
    setReusingId(recipientId);
    try {
      const state = await reuseRecipient(site, recipientId);
      if (!isCurrent(site)) return;
      setPayments(state);
      toast.success(t(`${prefix}.success.reused`));
    } catch (error) {
      if (!isCurrent(site)) return;
      if (isApiError(error, "RECIPIENT_ALREADY_LINKED")) alreadyLinked(site);
      else setReuseFailed(true);
    } finally {
      setReusingId(undefined);
    }
  };

  // The review may have moved on while the person did the check: closing it reloads the status
  const closeKyc = () => {
    setShowKyc(false);
    setReloadFailed(false);
    const site = siteId;
    getPayments(site)
      .then(value => isCurrent(site) && setPayments(value))
      .catch(() => isCurrent(site) && setReloadFailed(true));
  };

  const content = () => {
    if (payments === undefined) {
      return (
        <Card role="status" aria-label={t(`${prefix}.loading`)}>
          <Center>
            <KeepoLoader size={24} color="var(--color-neutral-white)" />
          </Center>
        </Card>
      );
    }
    if (payments === "unavailable" || payments === "error" || !payments.eligible) {
      const message = typeof payments === "string" ? payments : "notEligible";
      return (
        <Card>
          <Message>{t(`${prefix}.${message === "error" ? "loadError" : message}`)}</Message>
        </Card>
      );
    }
    if (payments.recipient) {
      return (
        <>
          {/* Refused is final at the provider: another registration (a new one or an approved one) takes its place */}
          {payments.recipient.status === "refused" && newCard("startAgain")}
          <RecipientCard
            recipient={payments.recipient}
            onKyc={() => setShowKyc(true)}
            onChangeBank={() => setShowBankModal(true)}
          />
          {reloadFailed && <ErrorMessage role="alert">{t(`${prefix}.loadError`)}</ErrorMessage>}
          {payments.recipient.status === "refused" && reusables(payments)}
        </>
      );
    }
    return (
      <>
        {payments.reusable.length === 0 && (
          <Card>
            <Message>{t(`${prefix}.intro`)}</Message>
          </Card>
        )}
        {newCard("start")}
        {reusables(payments)}
      </>
    );
  };

  /** A new registration: always first, as "Criar Keepo" on Meus Keepos */
  const newCard = (startLabel: "start" | "startAgain") => (
    <NewSiteCard type="button" disabled={!!reusingId} onClick={openForm}>
      <span className="icon">
        <PlusIcon width={14} height={14} color="currentColor" />
      </span>
      {t(`${prefix}.${startLabel}`)}
    </NewSiteCard>
  );

  /** The person's approved registrations to reuse */
  const reusables = (state: PaymentsState) => (
    <>
      {state.reusable.map(item => (
        <Card key={item.id}>
          <ReuseRow>
            <Initials aria-hidden>{initials(item.legalName)}</Initials>
            <div className="text">
              <p className="title">
                {orDash(item.legalName)}
                <Status $status="approved">{t(`${prefix}.status.approved`)}</Status>
              </p>
              <p className="description">
                {t(`${prefix}.reuseDetail`, {
                  documentLabel: t(`${chargesPrefix}.bank.cpf`),
                  document: orDash(item.document),
                  bank: orDash(item.bankLabel),
                })}
              </p>
            </div>
            <div className="end">
              <SmallButton
                type="button"
                aria-label={t(`${prefix}.reuse`, {
                  name: orDash(item.legalName),
                  document: orDash(item.document),
                  bank: orDash(item.bankLabel),
                })}
                $loading={reusingId === item.id}
                disabled={!!reusingId}
                onClick={() => reuse(item.id)}>
                {t(`${prefix}.useThis`)}
              </SmallButton>
            </div>
          </ReuseRow>
        </Card>
      ))}
      {reuseFailed && <ErrorMessage role="alert">{t(`${prefix}.errors.reuse`)}</ErrorMessage>}
    </>
  );

  return (
    <>
      {isRegistering && canRegister(payments) ? (
        <RecipientForm
          siteId={siteId}
          onDone={state => {
            if (!isCurrent(siteId)) return;
            setPayments(state);
            // Sent: back to Dados bancários by one way only. Registered, this state can't register again and the
            // <Navigate> below takes the person back; one refused at once leaves the form from here.
            if (canRegister(state)) navigate("..", { relative: "path", replace: true });
            const status = state.recipient?.status;
            // refused at once: said as it is (the card says why), never "sent for review"
            if (status === "refused") toast.error(t(`${prefix}.reason.refused`));
            else toast.success(t(`${prefix}.success.${status === "approved" ? "added" : "sent"}`));
          }}
          onAlreadyLinked={() => alreadyLinked(siteId)}
          onCancel={closeForm}
          header={header}
        />
      ) : isRegistering && payments !== undefined ? (
        // Already registered (and not refused) or not able to receive: the page itself says why
        <Navigate to=".." relative="path" replace />
      ) : (
        <Section>
          {/* only over the registration itself: the options need no title */}
          {typeof payments === "object" && payments.recipient && <Fonts.Label>{t(`${prefix}.section`)}</Fonts.Label>}
          {content()}
        </Section>
      )}
      {showKyc && (
        <KycModal
          siteId={siteId}
          onClose={closeKyc}
          onDecided={state => {
            if (!isCurrent(siteId)) return;
            setShowKyc(false);
            setReloadFailed(false);
            setPayments(state);
            if (state.recipient?.status === "approved") toast.success(t(`${prefix}.success.approved`));
            else toast.error(t(`${prefix}.reason.refused`));
          }}
        />
      )}
      {showBankModal && (
        <BankAccountModal
          siteId={siteId}
          sharedWith={
            typeof payments === "object" && payments.recipient?.sharedWith.length
              ? keepoNames(payments.recipient.sharedWith, t)
              : undefined
          }
          onDone={state => {
            setShowBankModal(false);
            if (!isCurrent(siteId)) return;
            setPayments(state);
            toast.success(t(`${prefix}.success.bankChanged`));
          }}
          onClose={() => setShowBankModal(false)}
        />
      )}
    </>
  );
};

/** What the API doesn't know yet (null, for a registration not sent): a dash. */
const orDash = (value: string | null) => value || "—";

/** A new registration only when there's none yet, or the last one was refused. */
const canRegister = (payments: PaymentsLoad): payments is PaymentsState =>
  typeof payments === "object" && payments.eligible && (!payments.recipient || payments.recipient.status === "refused");

/** The first letters of the first and last names (Mateus Vicente → MV), for the registration's little square. */
const initials = (name: string | null) => {
  const words = (name || "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  const last = words.length > 1 ? words[words.length - 1][0] : "";
  return (words[0][0] + last).toUpperCase();
};

const statusMessage = (recipient: PaymentRecipient) => (isKycStep(recipient) ? "kyc" : recipient.status);

/** The registration this Keepo receives by: who, where the money goes, and how the provider's review is going */
const RecipientCard = ({
  recipient,
  onKyc,
  onChangeBank,
}: {
  recipient: PaymentRecipient;
  onKyc: () => void;
  onChangeBank: () => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const readableReason =
    recipient.statusReason && !isProviderCode(recipient.statusReason) ? recipient.statusReason : undefined;
  // a draft has no account at the provider yet (it's changed by sending the registration); a refused one is replaced
  const canChangeBank = recipient.status !== "draft" && recipient.status !== "refused";
  const documentLabel = t(`${chargesPrefix}.bank.${recipient.personType === "pj" ? "cnpj" : "cpf"}`);

  return (
    <Card>
      <ReuseRow>
        <Initials aria-hidden>{initials(recipient.legalName)}</Initials>
        <div className="text">
          <p className="title">
            {orDash(recipient.legalName)}
            <Status $status={recipient.status}>{t(`${prefix}.status.${recipientStatusKey(recipient)}`)}</Status>
          </p>
          <p className="description">
            {documentLabel} {orDash(recipient.document)} · {orDash(recipient.bankLabel)}
          </p>
          {recipient.sharedWith.length > 0 && (
            <p className="description">{t(`${prefix}.sharedWith`, { keepos: keepoNames(recipient.sharedWith, t) })}</p>
          )}
        </div>
        {canChangeBank && (
          <div className="end">
            <SmallButton type="button" onClick={onChangeBank}>
              {t(`${prefix}.edit`)}
            </SmallButton>
          </div>
        )}
      </ReuseRow>
      {/* what to do, in our words (the provider's reason is its own text, in its language: only a detail) */}
      {recipient.status === "pending" && <Message>{t(`${prefix}.reason.pending`)}</Message>}
      {(recipient.status === "needs_info" || recipient.status === "refused") && (
        <>
          {/* one approved and then suspended was never "not approved" */}
          <Message>{t(`${prefix}.reason.${isStopped(recipient) ? "stopped" : statusMessage(recipient)}`)}</Message>
          {readableReason ? (
            <Message>{t(`${prefix}.reason.detail`, { reason: readableReason })}</Message>
          ) : (
            // a refusal with no reason to read: what to check
            recipient.status === "refused" &&
            !isStopped(recipient) && <Message>{t(`${prefix}.reason.refusedHelp`)}</Message>
          )}
        </>
      )}
      {/* refused: someone to ask */}
      {recipient.status === "refused" && (
        <>
          <div>
            <SmallButton type="button" onClick={() => navigate("/help")}>
              {t(`${prefix}.reason.support`)}
            </SmallButton>
          </div>
        </>
      )}
      {recipient.kycLinkAvailable && (
        <div>
          <SmallButton type="button" $primary onClick={onKyc}>
            {t(`${prefix}.kyc`)}
          </SmallButton>
        </div>
      )}
    </Card>
  );
};

/* A registration to reuse: initials, name over CPF and bank, status and its button on the right */
const ReuseRow = styled(Row)`
  .text {
    flex: 1;
  }

  /* the name and, beside it, its status */
  .title {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem 0.75rem;
    align-items: center;
  }

  .end {
    display: flex;
    flex-shrink: 0;
    gap: 1rem;
    align-items: center;
  }

  @media (max-width: 480px) {
    flex-wrap: wrap;

    .text {
      flex: 1;
    }

    .end {
      justify-content: space-between;
      width: 100%;
    }
  }
`;

const Initials = styled.span`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--color-neutral-white);
  background: var(--color-neutral-base);
  border-radius: 0.625rem;
`;
